import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, GitBranch, Clock, Package, Server } from 'lucide-react';

// Build metadata - Update these values for each deployment
const BUILD_INFO = {
  name: 'sprint10-v10.1.2',
  version: '10.1.2',
  timestamp: new Date().toISOString(),
  commitHash: 'sprint10-batch1.2-trial-ui',
  features: [
    'Sprint 6B: Lead Lifecycle & Handoff System',
    'Sprint 7: Backlog Fixes',
    'Sprint 8: Patient/Lead Separation + Immutability',
    'Sprint 9: Clinic Operations (Complete)',
    'Sprint 10 Batch 1.1: Clinic Package Model',
    'Sprint 10 Batch 1.2: Trial Activation UI'
  ],
  environment: process.env.NODE_ENV || 'development'
};

// Generate a deterministic hash from build info
const generateBuildHash = () => {
  const str = `${BUILD_INFO.name}-${BUILD_INFO.timestamp}-${BUILD_INFO.version}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).toUpperCase().padStart(8, '0');
};

const BuildInfo = () => {
  const buildHash = generateBuildHash();
  const buildTime = new Date(BUILD_INFO.timestamp);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-white/95 backdrop-blur shadow-2xl">
        <CardHeader className="text-center border-b pb-6">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-green-100 rounded-full">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">
            Build Verification
          </CardTitle>
          <p className="text-gray-500 mt-2">Deterministic build information for deployment verification</p>
        </CardHeader>
        
        <CardContent className="pt-6 space-y-6">
          {/* Build Name */}
          <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-center gap-3">
              <Package className="h-6 w-6 text-purple-600" />
              <div>
                <p className="text-sm text-gray-500 font-medium">Build Name</p>
                <p className="text-xl font-bold text-purple-700">{BUILD_INFO.name}</p>
              </div>
            </div>
            <Badge className="bg-purple-600 text-white text-lg px-4 py-1">v{BUILD_INFO.version}</Badge>
          </div>
          
          {/* Build Hash */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3">
              <GitBranch className="h-6 w-6 text-blue-600" />
              <div>
                <p className="text-sm text-gray-500 font-medium">Build Hash</p>
                <p className="text-xl font-mono font-bold text-blue-700">{buildHash}</p>
              </div>
            </div>
            <Badge variant="outline" className="border-blue-400 text-blue-600 font-mono">
              {BUILD_INFO.commitHash.substring(0, 12)}
            </Badge>
          </div>
          
          {/* Timestamp */}
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-green-600" />
              <div>
                <p className="text-sm text-gray-500 font-medium">Build Timestamp</p>
                <p className="text-lg font-bold text-green-700">
                  {buildTime.toISOString()}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="border-green-400 text-green-600">
              {buildTime.toLocaleDateString(undefined, { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Badge>
          </div>
          
          {/* Environment */}
          <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center gap-3">
              <Server className="h-6 w-6 text-orange-600" />
              <div>
                <p className="text-sm text-gray-500 font-medium">Environment</p>
                <p className="text-lg font-bold text-orange-700 uppercase">{BUILD_INFO.environment}</p>
              </div>
            </div>
          </div>
          
          {/* Features Included */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500 font-medium mb-3">Features Included</p>
            <div className="space-y-2">
              {BUILD_INFO.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Verification Footer */}
          <div className="text-center pt-4 border-t">
            <p className="text-sm text-gray-400">
              If you see this page, the build <strong className="text-purple-600">{BUILD_INFO.name}</strong> is deployed.
            </p>
            <p className="text-xs text-gray-400 mt-2 font-mono">
              Hash: {buildHash} | Generated: {new Date().toISOString()}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BuildInfo;
